import React from 'react';
import {
  Box, TextField, FormGroup, FormControlLabel, Checkbox, Typography, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { AUX_ADAPTERS, MUSIC_ITEMS, parseMusic, buildMusic } from './workshopOptions';

// Fields for one workshop — shared by the admin Leaders page and the
// leader's own form link. showAdminFields adds the admin-only maximum
// dancers field and "required space" note. value/onChange use the shape from emptyWorkshop(). When it
// runs comes from the leader's sessions, not the workshop.
export default function WorkshopFieldsEditor({ value, onChange, equipmentOptions = [], showAdminFields = false, disabled = false }) {
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const toggleEquipment = (item) => {
    const has = value.equipmentNeeded.includes(item);
    onChange({ ...value, equipmentNeeded: has ? value.equipmentNeeded.filter(x => x !== item) : [...value.equipmentNeeded, item] });
  };
  // Music is asked separately (tick every way that works, plus the adapter
  // for Aux), so its items aren't repeated in the checklist. Keep any saved
  // value that's since been removed from the options list.
  const options = [...new Set([...equipmentOptions, ...value.equipmentNeeded])].filter(item => !MUSIC_ITEMS.includes(item));
  const music = parseMusic(value.musicSetup);
  // Also clears music items ticked in the old checklist, so they don't linger hidden.
  const setMusic = (changes) => onChange({
    ...value,
    musicSetup: buildMusic({ ...music, ...changes }),
    equipmentNeeded: value.equipmentNeeded.filter(item => !MUSIC_ITEMS.includes(item)),
  });
  const musicBox = (key, label) => (
    <FormControlLabel disabled={disabled} sx={{ mr: 2, whiteSpace: 'nowrap' }}
      control={<Checkbox size="small" checked={music[key]} onChange={e => setMusic({ [key]: e.target.checked })} />}
      label={<Typography variant="body2">{label}</Typography>} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr' }, gap: 2 }}>
        <TextField label="Workshop title" size="small" required fullWidth value={value.title} onChange={set('title')} inputProps={{ maxLength: 120 }} disabled={disabled} />
        <TextField label="Dance style" size="small" fullWidth value={value.style} onChange={set('style')} inputProps={{ maxLength: 80 }} disabled={disabled} />
      </Box>
      <TextField label="Description" size="small" fullWidth multiline minRows={3} value={value.description} onChange={set('description')}
        inputProps={{ maxLength: 2000 }} helperText="Shown to teachers and dancers when they choose workshops." disabled={disabled} />
      {/* Admin only — leaders never see or set the dancer limit. */}
      {showAdminFields && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Maximum dancers (optional)" size="small" type="number" fullWidth value={value.maxDancers ?? ''}
            onChange={set('maxDancers')} inputProps={{ min: 1, max: 500 }} disabled={disabled} />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Which spaces it must or mustn’t use is set on the Schedule page (click the workshop).
          </Typography>
        </Box>
      )}
      <Box>
        <Typography variant="body2" color="text.secondary">How can you play music? Tick every way that works for you.</Typography>
        <FormGroup row sx={{ alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {musicBox('bluetooth', 'Bluetooth')}
          {musicBox('aux', 'Aux cable')}
          {music.aux && (
            <FormControl size="small" disabled={disabled} sx={{ flex: 1, minWidth: 180, my: 0.5 }}>
              <InputLabel>Aux adapter</InputLabel>
              <Select label="Aux adapter" value={music.adapter} onChange={e => setMusic({ adapter: e.target.value })}>
                {AUX_ADAPTERS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </FormGroup>
        {!value.musicSetup && <Typography variant="caption" color="text.secondary">Nothing ticked = no music needed.</Typography>}
      </Box>
      {options.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary">Other equipment needed</Typography>
          <FormGroup row>
            {options.map(item => (
              <FormControlLabel key={item} disabled={disabled}
                control={<Checkbox size="small" checked={value.equipmentNeeded.includes(item)} onChange={() => toggleEquipment(item)} />}
                label={<Typography variant="body2">{item}</Typography>} />
            ))}
          </FormGroup>
        </Box>
      )}
    </Box>
  );
}
